import { useState, useEffect, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { scoreColor } from "../utils/seoScore";

// Isolated redactor cabinet: queue of texts submitted by copywriters.
// Read the text, then Accept (→ SEO publishes) or send back for revisions.

const pathOf = (u) => { try { return new URL(u).pathname || u; } catch { return u; } };

export default function RedactorView() {
  const { apiFetch, dark, flash } = useApp();
  const [tab, setTab] = useState("queue"); // 'queue' | 'projects'
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [overview, setOverview] = useState([]);
  const [loadingOv, setLoadingOv] = useState(false);

  const c = {
    panel: dark ? "#17171f" : "#fff",
    border: dark ? "#2a2a35" : "#e5e7eb",
    text: dark ? "#e4e4ec" : "#1a1a2e",
    muted: dark ? "#8b8b9a" : "#6b7280",
  };

  const loadQueue = useCallback(() => {
    apiFetch("/api/copywriter/review-queue").then(r => r.json()).then(d => {
      setItems(d.items || []); setLoading(false);
    }).catch(() => setLoading(false));
  }, [apiFetch]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const loadOverview = useCallback(() => {
    setLoadingOv(true);
    apiFetch("/api/copywriter/overview").then(r => r.json()).then(d => { setOverview(d.projects || []); setLoadingOv(false); }).catch(() => setLoadingOv(false));
  }, [apiFetch]);

  // Load overview on mount too, so free projects are visible right away (banner on the queue tab).
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const freeProjects = overview.filter(p => p.owner_id == null);

  const openItem = (id) => {
    setComment("");
    apiFetch(`/api/copywriter/item/${id}`).then(r => r.json()).then(d => setSel(d)).catch(e => flash("Помилка: " + e.message));
  };

  const accept = () => {
    if (!sel) return;
    setBusy(true);
    apiFetch(`/api/copywriter/item/${sel.id}/accept`, { method: "POST" })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "err"); })
      .then(() => { flash("Прийнято ✓"); setSel(null); loadQueue(); })
      .catch(e => flash("Помилка: " + e.message)).finally(() => setBusy(false));
  };

  const sendBack = () => {
    if (!sel) return;
    if (!comment.trim()) { flash("Напишіть, що виправити"); return; }
    setBusy(true);
    apiFetch(`/api/copywriter/item/${sel.id}/revisions`, { method: "POST", body: JSON.stringify({ comment: comment.trim() }) })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "err"); })
      .then(() => { flash("Повернуто копірайтеру на правки ⟲"); setSel(null); loadQueue(); })
      .catch(e => flash("Помилка: " + e.message)).finally(() => setBusy(false));
  };

  const brief = sel?.brief || {};

  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{ background: tab === id ? "linear-gradient(135deg,#a855f7,#c084fc)" : "none", color: tab === id ? "#fff" : c.muted, border: `1px solid ${tab === id ? "#a855f7" : c.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {tabBtn("queue", "На перевірці")}
        {tabBtn("projects", "Проєкти (огляд)")}
      </div>

      {tab === "projects" ? (
        <div>
          <div style={{ fontSize: 12, color: c.muted, marginBottom: 12 }}>Хто з копірайтерів який проєкт узяв і що ще вільне.</div>
          {loadingOv ? <div style={{ color: c.muted }}>Завантаження…</div> :
            overview.length === 0 ? <div style={{ color: c.muted, padding: "20px 0" }}>Немає проєктів у роботі.</div> :
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
              {overview.map(p => {
                const free = p.owner_id == null;
                return (
                  <div key={p.project_id} style={{ background: c.panel, border: `1px solid ${free ? "#f59e0b" : c.border}`, borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 6 }}>{p.project_name}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: free ? "#f59e0b" : "#16a34a" }}>
                      {free ? "⚠ Вільний — ще не взято" : `✍ ${p.owner_name || "копірайтер"}`}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11 }}>
                      <span style={{ color: c.muted }}>Всього: <b style={{ color: c.text }}>{p.active_count}</b></span>
                      {Number(p.in_work) > 0 && <span style={{ color: "#f59e0b" }}>Пишуть: {p.in_work}</span>}
                      {Number(p.on_review) > 0 && <span style={{ color: "#0ea5e9" }}>На перевірці: {p.on_review}</span>}
                      {Number(p.revisions) > 0 && <span style={{ color: "#ef4444" }}>Правки: {p.revisions}</span>}
                    </div>
                    {p.nearest_deadline && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6 }}>⏱ дедлайн {String(p.nearest_deadline).slice(0, 10)}</div>}
                  </div>
                );
              })}
            </div>}
        </div>
      ) : (
      <>
        {freeProjects.length > 0 && (
          <div style={{ background: dark ? "#2a220f" : "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>🆓 Вільні проєкти (ще ніхто не взяв) · {freeProjects.length}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {freeProjects.map(p => (
                <span key={p.project_id} style={{ fontSize: 12, background: c.panel, border: `1px solid ${c.border}`, borderRadius: 6, padding: "4px 10px", color: c.text }}>
                  {p.project_name} · <b>{p.active_count}</b> текстів
                </span>
              ))}
            </div>
          </div>
        )}
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* Queue */}
      <div style={{ width: 300, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: c.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: ".5px" }}>На перевірці · {items.length}</div>
        {loading ? <div style={{ color: c.muted, fontSize: 13 }}>Завантаження…</div> :
          items.length === 0 ? <div style={{ color: c.muted, fontSize: 13, padding: "20px 0" }}>Черга порожня 🎉</div> :
          items.map(it => {
            const active = sel && sel.id === it.id;
            return (
              <div key={it.id} onClick={() => openItem(it.id)} style={{ padding: "10px 12px", marginBottom: 8, background: active ? (dark ? "#20202b" : "#eef2ff") : c.panel, border: `1px solid ${active ? "#6366f1" : c.border}`, borderRadius: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{it.project_name}</span>
                  {it.score != null && <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(Number(it.score)) }}>{it.score}</span>}
                </div>
                <div style={{ fontSize: 11, color: c.muted, wordBreak: "break-all", marginBottom: 3 }}>{pathOf(it.url)}</div>
                <div style={{ fontSize: 11, color: c.muted }}>✍ {it.writer_name || "—"}</div>
              </div>
            );
          })}
      </div>

      {/* Reader */}
      {!sel ? (
        <div style={{ flex: 1, textAlign: "center", color: c.muted, padding: "80px 0", fontSize: 14 }}>← Оберіть текст на перевірку</div>
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{sel.project_name}</div>
              <a href={sel.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#6366f1", wordBreak: "break-all" }}>{sel.url} ↗</a>
            </div>
            {sel.seo_score && sel.seo_score.total != null && (
              <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor(sel.seo_score.total) }}>Score {sel.seo_score.total}</span>
            )}
          </div>

          {(brief.keywords || []).length > 0 && (
            <div style={{ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: c.muted }}>
              <b style={{ color: c.text }}>ТЗ-ключі:</b> {(brief.keywords || []).slice(0, 20).map(k => k.keyword).join(" · ")}
            </div>
          )}

          {/* Rendered text on white */}
          <div style={{ background: "#fff", color: "#1a1a2e", border: `1px solid ${c.border}`, borderRadius: 8, padding: "20px 24px", fontSize: 15, lineHeight: 1.7, marginBottom: 14, overflowWrap: "break-word" }}
            dangerouslySetInnerHTML={{ __html: sel.html_content || "<i>Порожньо</i>" }} />

          {/* Actions */}
          <div style={{ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 10, padding: 14 }}>
            <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Коментар для копірайтера (потрібен, якщо повертаєте на правки)…"
              style={{ width: "100%", minHeight: 70, background: dark ? "#0f0f14" : "#fff", color: c.text, border: `1px solid ${c.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 10 }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={accept} disabled={busy} style={{ flex: 1, background: "linear-gradient(135deg,#16a34a,#22c55e)", border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>✓ Прийнято</button>
              <button onClick={sendBack} disabled={busy} style={{ flex: 1, background: dark ? "#3a1a1a" : "#fef2f2", border: "1px solid #ef4444", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, color: "#ef4444", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>⟲ На правки</button>
            </div>
          </div>
        </div>
      )}
    </div>
      </>
      )}
    </div>
  );
}
